import type { CSSProperties } from "react";
import { unitById } from "@zend/content";
import { portraitUrl } from "../assets/manifest.js";
import { formatCompact } from "../ui/Counter.js";
import { Stars, TierBadge, chassisName, roleName, mfrColor, tierColor, unitName } from "./shared.js";
import type { UnitView } from "./types.js";
import { ChassisIcon, RoleIcon, StatIcon } from "../assets/gameIcons.js";
import { Icon } from "../ui/Icon.js";
import { useT } from "../i18n/index.js";

export interface UnitCardProps {
  unit: UnitView;
  onClick?: () => void;
  selected?: boolean;
  compact?: boolean;
  /** Extra line under the name (e.g. the squad slot). */
  hint?: string | undefined;
  testId?: string | undefined;
}

/** Portrait card with the tier frame, stars, level, power and role glyphs (R20 tokens on every card). */
export function UnitCard({ unit, onClick, selected = false, compact = false, hint, testId }: UnitCardProps) {
  const t = useT();
  const def = unitById(unit.unitId);
  const color = tierColor(def.tier);
  return (
    <button type="button" className={`unit-card ${compact ? "compact" : ""} ${selected ? "selected" : ""} ${unit.wrecked ? "wrecked" : ""}`} style={{ "--unit-tier": color, "--unit-mfr": mfrColor(def.mfr) } as CSSProperties} onClick={onClick} data-testid={testId ?? `unit-${unit.unitId}`}>
      <div className="unit-portrait">
        <span className="unit-card-radiance" aria-hidden="true" />
        <img src={portraitUrl(unit.unitId)} alt={unitName(unit.unitId)} loading="lazy" decoding="async" draggable={false} style={{ position: "relative", objectFit: "contain" }} />
        <TierBadge tier={def.tier} style={{ position: "absolute", top: 4, left: 4 }} />
        <span className="unit-level" >Lv {unit.level}</span>
        {unit.wrecked ? <span className="unit-wrecked"><Icon name="warning" label={t("units.wrecked")} /></span> : unit.hpPct < 100 ? <span className="unit-hp" style={{ width: `${unit.hpPct}%` }} /> : null}
      </div>
      <div className="unit-card-body">
        <strong className="ellipsis" style={{ color: "#f1e8d4" }}>
          {unitName(unit.unitId)}
        </strong>
        <Stars stars={unit.stars} size={compact ? 9 : 11} />
        {!compact ? (
          <div className="row" style={{ gap: 6, fontSize: 11, color: "var(--text-dim)" }}>
            <span title={roleName(def.role)}><RoleIcon role={def.role} size={16} label={roleName(def.role)} /></span>
            <span title={chassisName(def.chassis)}><ChassisIcon chassis={def.chassis} size={16} label={chassisName(def.chassis)} /></span>
            <span style={{ color: mfrColor(def.mfr), fontWeight: 700 }}>{def.mfr}</span>
            <span className="grow" />
            <span className="mono" style={{ color: "var(--text)" }}>
              <StatIcon stat="power" size={14} />{formatCompact(unit.power)}
            </span>
          </div>
        ) : null}
        {hint ? <span className="hint">{hint}</span> : null}
      </div>
    </button>
  );
}
